import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import TableContainer from "../../../components/Common/DataTableContainer";
import Select from "react-select";
import { RepositoryFactory } from "../../../repositories";
import { formatProductoNombre } from "../../../utils/productoInventarioHelper";

const Materiales = () => {
    document.title = "Materiales | GraphicForce - Printesco";

    const [materiales, setMateriales] = useState([]);
    const [materialesInactivos, setMaterialesInactivos] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    // Removed tipos state
    const [modalAdd, setModalAdd] = useState(false);
    const [form, setForm] = useState({ nombre: "", texto_medida: "" });
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [materialToDelete, setMaterialToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [materialToRestore, setMaterialToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Estado para modal de edición de producto_inventario
    const [modalEditProductoInventario, setModalEditProductoInventario] = useState(false);
    const [materialToEdit, setMaterialToEdit] = useState(null);
    const [productosInventario, setProductosInventario] = useState([]);
    const [selectedProductoInventario, setSelectedProductoInventario] = useState(null);
    const [loadingProductos, setLoadingProductos] = useState(false);
    const [savingProductoInventario, setSavingProductoInventario] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Repositorio de producto_inventario
    const productoInventarioRepository = useMemo(() => RepositoryFactory.getRepository('producto_inventario'), []);

    // Removed tiposMap

    // Fetch todos los materiales y separarlos por estado
    const fetchMateriales = async () => {
        try {
            // Obtener todos los materiales de una vez con relación producto_inventario expandida
            const resultList = await pb.collection("material").getList(1, 1000, {
                sort: "-created",
                expand: "producto_inventario"
            });
            
            // Separar materiales activos e inactivos
            const activos = resultList.items.filter(material => material.activo !== false);
            const inactivos = resultList.items.filter(material => material.activo === false);
            
            // Aplicar paginación solo a los activos
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activosPaginados = activos.slice(startIndex, endIndex);
            
            setMateriales(activosPaginados);
            setMaterialesInactivos(inactivos);
            setTotalRecords(activos.length);
        } catch (err) {
            console.error("Error fetching materiales:", err);
        }
    };

    useEffect(() => {
        fetchMateriales();
    }, [pageIndex, pageSize]);

    const reloadMateriales = () => {
        fetchMateriales();
    };

    // Handler para abrir modal de edición de producto_inventario
    const handleEditProductoInventario = async (material) => {
        setMaterialToEdit(material);
        setLoadingProductos(true);
        setModalError("");
        
        try {
            // Cargar productos activos
            const productos = await productoInventarioRepository.getAllActive();
            setProductosInventario(productos);
            
            // Establecer el producto_inventario actual si existe
            if (material.producto_inventario && material.expand?.producto_inventario) {
                const currentProducto = productos.find(p => p.id === material.producto_inventario);
                if (currentProducto) {
                    setSelectedProductoInventario({
                        value: currentProducto.id,
                        label: formatProductoNombre(currentProducto)
                    });
                } else {
                    // Si el producto_inventario existe pero no está en la lista activa, usar el expand
                    const expandProducto = material.expand?.producto_inventario;
                    if (expandProducto) {
                        setSelectedProductoInventario({
                            value: expandProducto.id,
                            label: formatProductoNombre(expandProducto)
                        });
                    } else {
                        setSelectedProductoInventario({ value: "none", label: "Ninguno" });
                    }
                }
            } else {
                // Si no hay producto_inventario asignado, mostrar "Ninguno"
                setSelectedProductoInventario({ value: "none", label: "Ninguno" });
            }
            
            setModalEditProductoInventario(true);
        } catch (err) {
            console.error("Error loading productos inventario:", err);
            setModalError("Error al cargar productos de inventario");
        } finally {
            setLoadingProductos(false);
        }
    };

    // Handler para guardar asignación de producto_inventario
    const handleSaveProductoInventario = async (e) => {
        e.preventDefault();
        if (!materialToEdit) return;
        
        setSavingProductoInventario(true);
        setModalError("");
        
        try {
            // Si selectedProductoInventario es null o tiene value "none", establecer null
            const productoInventarioId = selectedProductoInventario && selectedProductoInventario.value !== "none" 
                ? selectedProductoInventario.value 
                : null;
            
            const updateData = {
                producto_inventario: productoInventarioId
            };
            
            await pb.collection("material").update(materialToEdit.id, updateData);
            fetchMateriales();
            setModalEditProductoInventario(false);
            setMaterialToEdit(null);
            setSelectedProductoInventario(null);
        } catch (err) {
            console.error("Error saving producto_inventario:", err);
            setModalError("Error al guardar la asignación de producto inventario");
        } finally {
            setSavingProductoInventario(false);
        }
    };

    // Handler para cerrar modal de edición
    const handleCloseEditProductoInventario = () => {
        setModalEditProductoInventario(false);
        setMaterialToEdit(null);
        setSelectedProductoInventario(null);
        setModalError("");
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "", texto_medida: "" });
        setModalError("");
        setModalAdd(true);
    };



    const handleRemove = (material) => {
        setMaterialToDelete(material);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!materialToDelete) return;
        setDeleting(true);
        try {
            await pb.collection("material").update(materialToDelete.id, { activo: false });
            fetchMateriales();
            setShowDeleteAlert(false);
            setMaterialToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar material");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setMaterialToDelete(null);
    };

    // Show alert and store material to restore
    const handleRestore = (material) => {
        setMaterialToRestore(material);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    // Confirm restore: set activo to true
    const confirmRestore = async () => {
        if (!materialToRestore) return;
        setRestoring(true);
        try {
            await pb.collection("material").update(materialToRestore.id, { activo: true });
            fetchMateriales();
            setShowRestoreAlert(false);
            setMaterialToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar material");
        }
        setRestoring(false);
    };

    // Cancel restore
    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setMaterialToRestore(null);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === "nombre") {
            setForm({ ...form, [name]: value.toUpperCase() });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    // No longer needed handleSelectMedida function

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        try {
            const data = {
                nombre: form.nombre,
                texto_medida: form.texto_medida,
                activo: true,
            };
            await pb.collection("material").create(data);
            fetchMateriales();
            setModalAdd(false);
        } catch (err) {
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `El material con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError("Error al agregar material");
            }
        }
        setSaving(false);
    };



    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Medida",
            accessorKey: "texto_medida",
            cell: info => info.getValue() || "-",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Producto Inventario",
            accessorKey: "producto_inventario",
            cell: info => {
                const material = info.row.original;
                const productoInventario = material.expand?.producto_inventario;
                if (!productoInventario) {
                    return "-";
                }
                return formatProductoNombre(productoInventario);
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const material = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="primary" size="sm" onClick={() => handleEditProductoInventario(material)} disabled={material.activo === false}>
                            <i className="fas fa-edit me-1"></i> Editar
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleRemove(material)} disabled={material.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [materiales]);

    // Columnas para materiales inactivos
    const columnsInactivos = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Medida",
            accessorKey: "texto_medida",
            cell: info => info.getValue() || "-",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const material = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(material)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [materialesInactivos]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Materiales" breadcrumbItem="Material" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && materialToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará el material <b>{materialToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && materialToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará el material <b>{materialToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Materiales
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadMateriales}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={materiales}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Tabla de Materiales Inactivos */}
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Materiales Inactivos
                                        <div>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                title="Recargar tabla"
                                                onClick={reloadMateriales}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columnsInactivos}
                                                data={materialesInactivos}
                                                isGlobalFilter={false}
                                                isPagination={false}
                                                isCustomPageSize={false}
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Material</ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                required
                            />
                        </FormGroup>
                        {/* Removed tipo_material field */}
                        <FormGroup>
                            <Label for="texto_medida">Medida</Label>
                            <Input
                                type="text"
                                name="texto_medida"
                                id="texto_medida"
                                value={form.texto_medida || ""}
                                onChange={handleChange}
                                placeholder="Ingrese la medida"
                                required
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Modal de Edición de Producto Inventario */}
            <Modal isOpen={modalEditProductoInventario} toggle={handleCloseEditProductoInventario} size="md">
                <ModalHeader toggle={handleCloseEditProductoInventario}>Editar Producto Inventario</ModalHeader>
                <Form onSubmit={handleSaveProductoInventario}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        {materialToEdit && (
                            <FormGroup>
                                <Label>Material</Label>
                                <Input
                                    type="text"
                                    value={formatProductoNombre(materialToEdit)}
                                    disabled
                                    className="mb-3"
                                />
                            </FormGroup>
                        )}
                        <FormGroup>
                            <Label for="producto_inventario">Producto Inventario</Label>
                            {loadingProductos ? (
                                <div className="text-center py-3">
                                    <Spinner size="sm" className="me-2" />
                                    Cargando productos...
                                </div>
                            ) : (
                                <Select
                                    id="producto_inventario"
                                    value={selectedProductoInventario}
                                    onChange={(option) => {
                                        // Si option es null (clear), establecer null
                                        // Si option.value es "none", establecer null
                                        if (!option || option.value === "none") {
                                            setSelectedProductoInventario(null);
                                        } else {
                                            setSelectedProductoInventario(option);
                                        }
                                    }}
                                    options={[
                                        { value: "none", label: "Ninguno" },
                                        ...productosInventario.map(p => ({
                                            value: p.id,
                                            label: formatProductoNombre(p)
                                        }))
                                    ]}
                                    placeholder="Seleccione un producto inventario..."
                                    isClearable={true}
                                    isSearchable={true}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            )}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={handleCloseEditProductoInventario} disabled={savingProductoInventario}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={savingProductoInventario || loadingProductos}>
                            {savingProductoInventario ? <Spinner size="sm" className="me-2" /> : null}
                            {savingProductoInventario ? "Guardando..." : "Guardar"}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default Materiales;